import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Activity as ActivityIcon, Mail, RefreshCw, AlertTriangle, Users, FileImage, Send } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";

const FREQUENCY = [
  { v: "off", label: "Off" },
  { v: "daily", label: "Daily" },
  { v: "weekly", label: "Weekly" },
];

export default function ProjectActivityTab({ projectId, projectDigestFrequency }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [freq, setFreq] = useState(projectDigestFrequency || "off");

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/activity-digest?days=${days}`);
      setData(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load activity"));
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [projectId, days]);

  const updateFreq = async (v) => {
    setFreq(v);
    try {
      await api.patch(`/projects/${projectId}/digest-prefs`, { frequency: v });
      toast.success(`Digest preference: ${v}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't save preference")); }
  };

  return (
    <div className="space-y-4" data-testid="project-activity-tab">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="activity-range"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Last 24h</SelectItem>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="rounded-none h-8" onClick={load} data-testid="activity-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>

        <div className="flex items-center gap-2 ml-auto">
          <span className="text-[10px] text-muted-foreground uppercase tracking-widest">Digest schedule</span>
          <Select value={freq} onValueChange={updateFreq}>
            <SelectTrigger className="h-8 w-28 text-xs" data-testid="activity-freq"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FREQUENCY.map((f) => <SelectItem key={f.v} value={f.v}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <SendDigestDialog projectId={projectId} days={days} totals={data?.totals} />
        </div>
      </div>

      {busy && !data ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : data ? (
        <>
          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile label="New items" value={data.totals.new_items} icon={FileImage} testid="activity-stat-new" />
            <StatTile label="Pending approval" value={data.totals.pending} icon={ActivityIcon} testid="activity-stat-pending" />
            <StatTile label="Events" value={data.totals.activity_events} icon={ActivityIcon} testid="activity-stat-events" />
            <StatTile label="Low-rated" value={(data.low_rated || []).length} icon={AlertTriangle} testid="activity-stat-low" />
          </div>

          {/* New items */}
          <Section title="New review items" empty="No new items in this window." count={data.new_items.length}>
            <ul className="divide-y divide-border" data-testid="activity-new-items">
              {data.new_items.map((it) => (
                <li key={it.id} className="py-2 flex items-center justify-between text-sm" data-testid={`activity-new-${it.id}`}>
                  <Link to={`/app/review-items/${it.id}`} className="hover:text-primary truncate">
                    <span className="font-medium">{it.title}</span> <span className="text-muted-foreground text-xs">· {it.item_type}</span>
                  </Link>
                  <span className="text-[10px] text-muted-foreground">{fmtRelative(it.created_at)}</span>
                </li>
              ))}
            </ul>
          </Section>

          {/* Pending */}
          <Section title="Pending approval" empty="Nothing pending." count={data.pending_approval.length}>
            <ul className="divide-y divide-border" data-testid="activity-pending">
              {data.pending_approval.map((it) => (
                <li key={it.id} className="py-2 flex items-center justify-between text-sm">
                  <Link to={`/app/review-items/${it.id}`} className="hover:text-primary truncate">{it.title}</Link>
                  <span className="chip chip-pending text-[10px]">{(it.approval_status || "pending").replace("_", " ")}</span>
                </li>
              ))}
            </ul>
          </Section>

          {/* Low-rated */}
          {(data.low_rated || []).length > 0 && (
            <Section title="Low-rated items" empty="" count={data.low_rated.length}>
              <ul className="divide-y divide-border" data-testid="activity-low-rated">
                {data.low_rated.map((it) => (
                  <li key={it.review_item_id} className="py-2 flex items-center justify-between text-sm">
                    <Link to={`/app/review-items/${it.review_item_id}`} className="hover:text-primary truncate">{it.title}</Link>
                    <span className="text-amber-400 text-xs">{it.avg_stars}★</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Top reviewers */}
          {data.top_reviewers && data.top_reviewers.length > 0 && (
            <Section title="Top reviewers" empty="" count={data.top_reviewers.length} icon={Users}>
              <ul className="divide-y divide-border" data-testid="activity-top-reviewers">
                {data.top_reviewers.map((r) => (
                  <li key={r.email} className="py-2 flex items-center justify-between text-sm">
                    <span className="truncate">{r.email}</span>
                    <span className="text-xs text-muted-foreground">{r.events} events</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Activity feed */}
          <Section title="Recent activity" empty="No activity in this window." count={data.activity.length}>
            <ul className="divide-y divide-border max-h-96 overflow-auto" data-testid="activity-feed">
              {data.activity.map((a) => (
                <li key={a.id} className="py-2 text-sm" data-testid={`activity-event-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <span><strong>{a.actor_name || "system"}</strong> · {a.action}</span>
                    <span className="text-[10px] text-muted-foreground">{fmtRelative(a.created_at)}</span>
                  </div>
                  {a.target_label && <div className="text-xs text-muted-foreground truncate">{a.target_label}</div>}
                </li>
              ))}
            </ul>
          </Section>
        </>
      ) : null}
    </div>
  );
}

function StatTile({ label, value, icon: Icon, testid }) {
  return (
    <div className="border border-border p-3 flex items-start gap-3" data-testid={testid}>
      <Icon className="w-4 h-4 text-primary mt-0.5" />
      <div>
        <div className="text-2xl font-bold leading-none" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
        <div className="label-eyebrow text-[9px] mt-1">{label}</div>
      </div>
    </div>
  );
}

function Section({ title, children, empty, count, icon: Icon }) {
  return (
    <div className="border border-border" data-testid={`activity-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="px-3 py-2 border-b border-border bg-muted/40 flex items-center justify-between">
        <div className="label-eyebrow text-[10px] inline-flex items-center gap-1.5">
          {Icon && <Icon className="w-3 h-3" />} {title}
        </div>
        <span className="text-[10px] text-muted-foreground">{count}</span>
      </div>
      <div className="px-3 py-2">
        {count === 0 && empty ? <div className="text-xs text-muted-foreground py-2">{empty}</div> : children}
      </div>
    </div>
  );
}

function SendDigestDialog({ projectId, days, totals }) {
  const [open, setOpen] = useState(false);
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const list = emails.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) return toast.error("Add at least one email");
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/send-digest`, { recipients: list, days });
      const okCount = (data.results || []).filter((r) => r.ok && r.sent).length;
      const failCount = (data.results || []).length - okCount;
      toast.success(`Digest queued · sent ${okCount}${failCount ? `, ${failCount} skipped/logged` : ""}`);
      setOpen(false);
      setEmails("");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't send digest"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-none h-8" data-testid="activity-send-digest-trigger">
          <Mail className="w-3.5 h-3.5 mr-1.5" /> Send digest now
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="send-digest-dialog">
        <DialogHeader><DialogTitle>Email project digest</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Window: last {days} days · {totals?.new_items || 0} new, {totals?.pending || 0} pending, {totals?.activity_events || 0} events.
          </p>
          <div>
            <div className="label-eyebrow mb-1">Recipients (comma or space separated)</div>
            <Input
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder="lead@example.com, client@acme.com"
              data-testid="send-digest-recipients"
              autoFocus
            />
          </div>
          <p className="text-[10px] text-muted-foreground">
            Uses your workspace email provider (configured in Super Admin → Email). When provider is &quot;log only&quot;, emails are recorded but not delivered.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)} data-testid="send-digest-cancel">Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy || !emails.trim()} data-testid="send-digest-submit">
            <Send className="w-3.5 h-3.5 mr-1.5" /> {busy ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
